import Big from 'big.js';

/** A decimal as the API sends it: a JSON string with a dot ("1565.5"). Never converted to a JS number. */
export type Decimal = string;

Big.DP = 20;
Big.RM = Big.roundHalfUp;

export function big(value: Decimal | number | null | undefined): Big {
  if (value === null || value === undefined || value === '') return new Big(0);
  return new Big(value);
}

export function isDecimal(value: string): boolean {
  try {
    new Big(value);
    return true;
  } catch {
    return false;
  }
}

function group(integer: string): string {
  return integer.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

export interface DecimalFormat {
  /** Minimum fraction digits (default 0). */
  min?: number;
  /** Maximum fraction digits (default 2); rounds half up. */
  max?: number;
  /** Prefix a + for positive values. */
  signed?: boolean;
}

/** "1565.5" → "1.565,50" with {min:2}. String based, so 20-digit amounts are exact. */
export function formatDecimal(value: Decimal | number | null | undefined, opts: DecimalFormat = {}): string {
  if (value === null || value === undefined || value === '') return '';
  const min = opts.min ?? 0;
  const max = Math.max(opts.max ?? 2, min);
  const rounded = big(value).round(max, Big.roundHalfUp);
  const negative = rounded.lt(0);
  const [integer = '0', digits = ''] = rounded.abs().toFixed(max).split('.');
  let fraction = digits;
  fraction = fraction.replace(/0+$/, '');
  if (fraction.length < min) fraction = fraction.padEnd(min, '0');
  const body = group(integer) + (fraction ? ',' + fraction : '');
  if (negative && body.replace(/[0.,]/g, '') !== '') return '−' + body;
  return (opts.signed && rounded.gt(0) ? '+' : '') + body;
}

/**
 * Parses what a person types in Argentina into the API's dot decimal, or null when it is not a number.
 * "1.565,50" → "1565.50"; "1565,5" → "1565.5"; "1565.50" → "1565.50" (a dot followed by other than
 * exactly three digits is a decimal point, as typed on a numeric keypad). For amounts "1.565" is one
 * thousand five hundred sixty-five; measures pass `dotThousands: false` so "120.5" and "1.565" are decimals.
 */
export function parseDecimalInput(raw: string, { dotThousands = true } = {}): Decimal | null {
  let text = raw.trim().replace(/\s|\$|US\$|USD|ARS/gi, '');
  if (!text) return null;
  const negative = /^[-−]/.test(text);
  text = text.replace(/^[-−+]/, '');
  if (!/^[\d.,]+$/.test(text)) return null;
  if (text.includes(',')) {
    // Comma is the decimal separator; dots are thousands.
    const [integer, ...rest] = text.split(',');
    if (rest.length > 1 || integer === undefined) return null;
    if (integer.includes('.') && !/^\d{1,3}(\.\d{3})+$/.test(integer)) return null;
    text = integer.replace(/\./g, '') + '.' + (rest[0] ?? '');
  } else if (
    /^\d{1,3}(\.\d{3})+$/.test(text) &&
    (dotThousands || (text.match(/\./g)?.length ?? 0) > 1)
  ) {
    text = text.replace(/\./g, '');
  } else if ((text.match(/\./g)?.length ?? 0) > 1) {
    return null;
  }
  if (text.endsWith('.')) text = text.slice(0, -1);
  if (text.startsWith('.')) text = '0' + text;
  if (!isDecimal(text)) return null;
  return (negative ? '-' : '') + text;
}

export function compareDecimal(a: Decimal, b: Decimal): number {
  return big(a).cmp(big(b));
}
